import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import type { UdgspzEntityMapStylePayload, UdgspzPatternOption } from "@/types/udgspz-entity";

type UdgspzEntityMapStyleFormProps = {
  disabled?: boolean;
  title?: string;
  clearLabel?: string;
  fillColor: string;
  lineColor: string;
  patternCode: string;
  lineWidth: number;
  lineDashCode: string;
  labelVisible?: boolean;
  labelTextMode?: string;
  labelText?: string;
  labelColor?: string;
  labelSize?: number;
  labelTextAnchor?: string;
  labelOffsetX?: number;
  labelOffsetY?: number;
  labelHaloColor?: string;
  labelHaloWidth?: number;
  patterns: UdgspzPatternOption[];
  dashOptions: UdgspzPatternOption[];
  textModeOptions?: UdgspzPatternOption[];
  textAnchorOptions?: UdgspzPatternOption[];
  onSave: (payload: UdgspzEntityMapStylePayload) => Promise<void>;
  onClear: () => Promise<void>;
};

const DEFAULT_TEXT_MODES: UdgspzPatternOption[] = [
  { code: "entity_name", label: "Имя сущности" },
  { code: "custom", label: "Свой текст" },
];

const DEFAULT_ANCHORS: UdgspzPatternOption[] = [
  { code: "center", label: "По центру" },
  { code: "left", label: "Слева" },
  { code: "right", label: "Справа" },
  { code: "top", label: "Сверху" },
  { code: "bottom", label: "Снизу" },
  { code: "top-left", label: "Сверху слева" },
  { code: "top-right", label: "Сверху справа" },
  { code: "bottom-left", label: "Снизу слева" },
  { code: "bottom-right", label: "Снизу справа" },
];

export function UdgspzEntityMapStyleForm({
  disabled = false,
  title = "Стиль этой сущности. Пустые поля наследуют тип.",
  clearLabel = "Сбросить к типу",
  fillColor,
  lineColor,
  patternCode,
  lineWidth,
  lineDashCode,
  labelVisible = true,
  labelTextMode = "entity_name",
  labelText = "",
  labelColor = "#0f172a",
  labelSize = 13,
  labelTextAnchor = "center",
  labelOffsetX = 0,
  labelOffsetY = 0,
  labelHaloColor = "#ffffff",
  labelHaloWidth = 2,
  patterns,
  dashOptions,
  textModeOptions = DEFAULT_TEXT_MODES,
  textAnchorOptions = DEFAULT_ANCHORS,
  onSave,
  onClear,
}: UdgspzEntityMapStyleFormProps) {
  const [fill, setFill] = useState(fillColor);
  const [line, setLine] = useState(lineColor);
  const [pattern, setPattern] = useState(patternCode);
  const [width, setWidth] = useState(String(lineWidth));
  const [dash, setDash] = useState(lineDashCode);
  const [visible, setVisible] = useState(labelVisible);
  const [textMode, setTextMode] = useState(labelTextMode);
  const [customText, setCustomText] = useState(labelText);
  const [labColor, setLabColor] = useState(labelColor);
  const [size, setSize] = useState(String(labelSize));
  const [anchor, setAnchor] = useState(labelTextAnchor);
  const [offsetX, setOffsetX] = useState(String(labelOffsetX));
  const [offsetY, setOffsetY] = useState(String(labelOffsetY));
  const [haloColor, setHaloColor] = useState(labelHaloColor);
  const [haloWidth, setHaloWidth] = useState(String(labelHaloWidth));
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setFill(fillColor);
    setLine(lineColor);
    setPattern(patternCode);
    setWidth(String(lineWidth));
    setDash(lineDashCode);
    setVisible(labelVisible);
    setTextMode(labelTextMode);
    setCustomText(labelText);
    setLabColor(labelColor);
    setSize(String(labelSize));
    setAnchor(labelTextAnchor);
    setOffsetX(String(labelOffsetX));
    setOffsetY(String(labelOffsetY));
    setHaloColor(labelHaloColor);
    setHaloWidth(String(labelHaloWidth));
  }, [
    fillColor,
    lineColor,
    patternCode,
    lineWidth,
    lineDashCode,
    labelVisible,
    labelTextMode,
    labelText,
    labelColor,
    labelSize,
    labelTextAnchor,
    labelOffsetX,
    labelOffsetY,
    labelHaloColor,
    labelHaloWidth,
  ]);

  const save = async () => {
    const parsedWidth = Number(width);
    const parsedSize = Number(size);
    const parsedOffsetX = Number(offsetX);
    const parsedOffsetY = Number(offsetY);
    const parsedHaloWidth = Number(haloWidth);
    if (!fill || !line || !pattern || !dash || !Number.isFinite(parsedWidth) || parsedWidth <= 0) {
      return;
    }
    if (!Number.isFinite(parsedSize) || parsedSize <= 0 || !labColor || !anchor || !haloColor) {
      return;
    }
    if (!Number.isFinite(parsedOffsetX) || !Number.isFinite(parsedOffsetY)) {
      return;
    }
    if (!Number.isFinite(parsedHaloWidth) || parsedHaloWidth < 0) {
      return;
    }
    setPending(true);
    try {
      await onSave({
        fill_color: fill,
        line_color: line,
        pattern_code: pattern,
        line_width: parsedWidth,
        line_dash_code: dash,
        label_visible: visible,
        label_text_mode: textMode,
        label_text: textMode === "custom" ? customText : null,
        label_color: labColor,
        label_size: parsedSize,
        label_text_anchor: anchor,
        label_offset_x: parsedOffsetX,
        label_offset_y: parsedOffsetY,
        label_halo_color: haloColor,
        label_halo_width: parsedHaloWidth,
      });
    } finally {
      setPending(false);
    }
  };

  const clear = async () => {
    setPending(true);
    try {
      await onClear();
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-border p-2">
      <p className="text-xs text-foreground">{title}</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor="udg-style-fill" className="text-xs">
            Заливка
          </Label>
          <Input
            id="udg-style-fill"
            type="color"
            className="h-8 w-14 p-1"
            value={fill || "#64748b"}
            disabled={disabled}
            onChange={(event) => setFill(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="udg-style-line" className="text-xs">
            Обводка
          </Label>
          <Input
            id="udg-style-line"
            type="color"
            className="h-8 w-14 p-1"
            value={line || "#475569"}
            disabled={disabled}
            onChange={(event) => setLine(event.target.value)}
          />
        </div>
        <div className="min-w-[140px] space-y-1">
          <Label className="text-xs">Рисунок</Label>
          <Select value={pattern || undefined} onValueChange={setPattern} disabled={disabled}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Как у типа" />
            </SelectTrigger>
            <SelectContent>
              {patterns.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="udg-style-width" className="text-xs">
            Толщина
          </Label>
          <Input
            id="udg-style-width"
            type="number"
            min={0.5}
            max={48}
            step={0.5}
            className="h-8 w-20"
            value={width}
            disabled={disabled}
            onChange={(event) => setWidth(event.target.value)}
          />
        </div>
        <div className="min-w-[150px] space-y-1">
          <Label className="text-xs">Линия</Label>
          <Select value={dash || undefined} onValueChange={setDash} disabled={disabled}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Как у типа" />
            </SelectTrigger>
            <SelectContent>
              {dashOptions.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2 border-t border-border pt-2">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-medium text-foreground">Подпись на карте</p>
          <SectionHelpTooltip label="Справка: подпись на карте">
            <p className="font-medium text-foreground">Зачем</p>
            <p>
              Текст у контура: видимость, содержимое, цвет, размер, якорь, смещение и обводка (halo).
              Каскад: геометрия → сущность → тип → значения по умолчанию.
            </p>
            <p className="font-medium text-foreground">Как работать</p>
            <ul className="list-disc space-y-1 pl-4">
              <li>Снимите «Показывать», чтобы скрыть подпись на карте.</li>
              <li>«Свой текст» — произвольная строка; пустой текст снова покажет имя сущности.</li>
              <li>Смещение в em относительно якоря (MapLibre text-offset).</li>
            </ul>
          </SectionHelpTooltip>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex h-8 items-center gap-2 text-xs">
            <Checkbox
              checked={visible}
              disabled={disabled}
              onCheckedChange={(value) => setVisible(value === true)}
            />
            Показывать
          </label>
          <div className="min-w-[150px] space-y-1">
            <Label className="text-xs">Текст</Label>
            <Select value={textMode || undefined} onValueChange={setTextMode} disabled={disabled}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {textModeOptions.map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {textMode === "custom" ? (
            <div className="min-w-[160px] flex-1 space-y-1">
              <Label htmlFor="udg-label-text" className="text-xs">
                Свой текст
              </Label>
              <Input
                id="udg-label-text"
                className="h-8"
                value={customText}
                disabled={disabled}
                onChange={(event) => setCustomText(event.target.value)}
                placeholder="Подпись"
              />
            </div>
          ) : null}
          <div className="space-y-1">
            <Label htmlFor="udg-label-color" className="text-xs">
              Цвет
            </Label>
            <Input
              id="udg-label-color"
              type="color"
              className="h-8 w-14 p-1"
              value={labColor || "#0f172a"}
              disabled={disabled}
              onChange={(event) => setLabColor(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="udg-label-size" className="text-xs">
              Размер
            </Label>
            <Input
              id="udg-label-size"
              type="number"
              min={8}
              max={96}
              step={1}
              className="h-8 w-16"
              value={size}
              disabled={disabled}
              onChange={(event) => setSize(event.target.value)}
            />
          </div>
          <div className="min-w-[150px] space-y-1">
            <Label className="text-xs">Якорь</Label>
            <Select value={anchor || undefined} onValueChange={setAnchor} disabled={disabled}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {textAnchorOptions.map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="udg-label-ox" className="text-xs">
              Смещение X
            </Label>
            <Input
              id="udg-label-ox"
              type="number"
              step={0.1}
              className="h-8 w-16"
              value={offsetX}
              disabled={disabled}
              onChange={(event) => setOffsetX(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="udg-label-oy" className="text-xs">
              Смещение Y
            </Label>
            <Input
              id="udg-label-oy"
              type="number"
              step={0.1}
              className="h-8 w-16"
              value={offsetY}
              disabled={disabled}
              onChange={(event) => setOffsetY(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="udg-label-halo" className="text-xs">
              Halo
            </Label>
            <Input
              id="udg-label-halo"
              type="color"
              className="h-8 w-14 p-1"
              value={haloColor || "#ffffff"}
              disabled={disabled}
              onChange={(event) => setHaloColor(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="udg-label-halo-w" className="text-xs">
              Halo px
            </Label>
            <Input
              id="udg-label-halo-w"
              type="number"
              min={0}
              max={24}
              step={0.5}
              className="h-8 w-16"
              value={haloWidth}
              disabled={disabled}
              onChange={(event) => setHaloWidth(event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={disabled || pending || !fill || !line || !pattern || !dash}
          onClick={() => void save()}
        >
          Сохранить стиль
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={disabled || pending} onClick={() => void clear()}>
          {clearLabel}
        </Button>
      </div>
    </div>
  );
}
